import { useState, useEffect } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { Header, Footer, PasswordInput } from '@ui';
import { getSupabaseClient } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingCode, setValidatingCode] = useState(true);
  const [codeError, setCodeError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Parse hash fragment and set session
    const handleSession = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type');

      if (!accessToken || !refreshToken || type !== 'recovery') {
        setCodeError('No reset token found. Please request a new password reset link.');
        setValidatingCode(false);
        return;
      }

      try {
        const supabase = getSupabaseClient();
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (error) {
          setCodeError('Invalid or expired reset link. Please request a new one.');
        }
      } catch (err) {
        console.error('Session setup error:', err);
        setCodeError('Something went wrong. Please try again.');
      } finally {
        setValidatingCode(false);
      }
    };

    handleSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        setSuccess(true);
        setLoading(false);
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      console.error('Password update error:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const inputClasses =
    'w-full border border-[#2C2731] rounded-xl px-4 py-3 text-sm bg-[#FEF6D6] placeholder:text-[#2C2731]/50 focus:outline-none focus:ring-2 focus:ring-[#2C2731] focus:border-[#2C2731] transition-colors';

  if (validatingCode) {
    return (
      <div className="min-h-screen flex flex-col bg-[#FFFCEF] text-[#2C2731]">
        <Header variant="login" />
        <main className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="max-w-md w-full text-center">
            <div className="animate-spin h-12 w-12 border-4 border-[#2C2731] border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-[#2C2731]">Validating reset link...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (codeError) {
    return (
      <div className="min-h-screen flex flex-col bg-[#FFFCEF] text-[#2C2731]">
        <Header variant="login" />
        <main className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="max-w-md w-full text-center border border-[#2C2731] rounded-2xl bg-[#FFFCEF] px-8 py-10 shadow-sm">
            <div className="p-4 bg-[#2C2731] border border-[#FEF6D6] rounded-xl text-[#FEF6D6] mb-6">
              <div className="flex">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="h-5 w-5 text-[#FEF6D6]" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">{codeError}</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate('/auth/forgot-password')}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#304031] text-sm font-semibold transition bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90"
            >
              Request New Reset Link
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFCEF] text-[#2C2731]">
      <Header variant="login" />

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="max-w-md w-full text-center border border-[#2C2731] rounded-2xl bg-[#FFFCEF] px-8 py-10 shadow-sm">
          {/* Icon */}
          <div className="h-14 w-14 mx-auto flex items-center justify-center rounded-full bg-[#E8DFBD] text-[#2C2731] mb-8">
            <LockClosedIcon className="h-6 w-6 text-[#2C2731]" />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-medium text-[#2C2731] mb-2 font-recoleta">
            Set New Password
          </h1>
          <p className="text-[#2C2731]/80 mb-8">
            Enter your new password below.
          </p>

          {success ? (
            <div className="text-left">
              <div className="p-4 bg-[#E8DFBD] border border-[#2C2731] rounded-xl text-[#2C2731] mb-6">
                <div className="flex">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="h-5 w-5 text-[#2C2731]" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-[#2C2731]">Password updated successfully!</h3>
                    <p className="text-sm mt-2">
                      Redirecting you to login...
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form className="text-left space-y-4" onSubmit={handleSubmit}>
              {error && (
                <div className="p-4 bg-[#2C2731] border border-[#FEF6D6] rounded-xl text-[#FEF6D6]">
                  <div className="flex">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="h-5 w-5 text-[#FEF6D6]" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#2C2731] mb-1">
                  New Password
                </label>
                <PasswordInput
                  placeholder="Enter your new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClasses}
                  required
                  minLength={8}
                />
                <p className="text-xs text-[#2C2731]/60 mt-1">
                  Must be at least 8 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2731] mb-1">
                  Confirm Password
                </label>
                <PasswordInput
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClasses}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#304031] text-sm font-semibold transition ${
                  loading
                    ? 'bg-[#304031]/70 text-[#fef6d6] cursor-not-allowed'
                    : 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin h-5 w-5 border-2 border-[#FEF6D6] border-t-transparent rounded-full"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <LockClosedIcon className="h-5 w-5 text-[#FEF6D6]" />
                    Update Password
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
